// Sugalarm — landscape IOB pager (cycle 114).
//
// Two-page horizontal swipe pager for the mobile landscape Now stats
// column. Page 1 is the existing <BolusRings> (rings only — the textual
// detail lives on page 2, so the lines aren't duplicated; BolusRings is
// untouched, `children` is simply omitted, and the cycle-43
// ActiveBolusesModal tap flow is preserved via `onRingsTap`). Page 2 is
// a compact, read-only "insulin & food" detail card built entirely from
// data already on hand in NowScreen — no new fetch.
//
// Gesture: plain PointerEvents, no library. A horizontal swipe (|dx| >
// 40 px, horizontal-dominant) commits a page change; < 8 px is a tap and
// falls through to the rings' own onClick. Vertical-dominant moves are
// left to the column's native scroll (`touch-action: pan-y`). Page dots
// double as tappable navigation. Pager state is session-local and resets
// to page 1 on mount (the pager only mounts in landscape; rotating to
// portrait unmounts it).

import { BolusRings } from "./BolusRings";
import {
  CORRECTION_BOLUS_COLOR,
  MEAL_BOLUS_COLOR,
  iobTimeRemainingMs,
  type BasalContext,
  type BolusForRings,
} from "../../lib/iob";
import { fmtTime } from "../../lib/glucose";
import { usePagerSwipe } from "../../hooks/usePagerSwipe";
import type { Treatment } from "../../lib/types";
import styles from "./IobPager.module.css";

interface Props {
  /** The colored ring set NowScreen already computed (meal vs correction
   *  encoded in `color`). Page 1 renders these; page 2 lists them. */
  boluses: BolusForRings[];
  /** All loaded treatments — page 2 derives recent carbs from these. */
  treatments: Treatment[];
  now: number;
  h24: boolean;
  /** Cycle 43 ActiveBolusesModal opener — wired only when there are
   *  active boluses (NowScreen decides). */
  onRingsTap?: () => void;
  /** Cycle 147: the long-acting dose on board — threads to BolusRings'
   *  third outer ring. */
  basal?: BasalContext | null;
  /** Cycle 163.1: threaded to BolusRings — glass × on the due nudge. */
  onDismissDue?: () => void;
  /** Cycle 303: threaded to BolusRings — glass × dismissing the whole card. */
  onDismissCard?: () => void;
}

// Page 2 food window: carbs logged within the last 4 hours (matches the
// IOB horizon, but named for the food list it governs).
const FOOD_WINDOW_MS = 4 * 60 * 60 * 1000;
// Combined visible-row cap before a "+N more" line (keeps page 2 scroll-
// free at the rings-card footprint).
const MAX_ROWS = 5;

function fmtRemaining(ms: number): string {
  const totalMin = Math.max(1, Math.ceil(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

interface InsulinRow {
  units: number;
  takenAt: number;
  isCorrection: boolean;
  remainingMs: number;
}

interface FoodRow {
  carbsG: number;
  takenAt: number;
}

export function IobPager({
  boluses,
  treatments,
  now,
  h24,
  onRingsTap,
  basal = null,
  onDismissDue,
  onDismissCard,
}: Props) {
  // Cycle 122: the tap-vs-swipe contract is now the shared usePagerSwipe
  // hook (extracted from this component); behavior is byte-identical.
  const { page, goToPage, onPointerDown, onPointerMove, onPointerUp, onPointerCancel } =
    usePagerSwipe();

  // ── Page 2 data (read-only; no fetch) ──────────────────────────────
  // Insulin: the same ring set, newest-first. meal/correction is read
  // from the ring color (set via isCorrectionBolus in NowScreen — single
  // source of truth). Per-bolus remaining uses the shared countdown math.
  const insulinRows: InsulinRow[] = boluses
    .map((b) => ({
      units: b.units,
      takenAt: b.takenAt,
      isCorrection: b.color === CORRECTION_BOLUS_COLOR,
      remainingMs: iobTimeRemainingMs([{ takenAt: b.takenAt }], now),
    }))
    .sort((a, b) => b.takenAt - a.takenAt);

  const foodRows: FoodRow[] = treatments
    .filter(
      (t) =>
        t.event_type === "carbs" &&
        t.carbs_g != null &&
        Number(t.carbs_g) > 0 &&
        new Date(t.timestamp).getTime() >= now - FOOD_WINDOW_MS,
    )
    .map((t) => ({
      carbsG: Number(t.carbs_g),
      takenAt: new Date(t.timestamp).getTime(),
    }))
    .sort((a, b) => b.takenAt - a.takenAt);

  const isEmpty = insulinRows.length === 0 && foodRows.length === 0;
  // Combined cap: insulin rows always shown (≤2), food fills the rest.
  const foodBudget = Math.max(0, MAX_ROWS - insulinRows.length);
  const visibleFood = foodRows.slice(0, foodBudget);
  const moreCount = foodRows.length - visibleFood.length;

  return (
    <div className={styles.pager}>
      <div
        className={styles.viewport}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <div
          className={styles.track}
          style={{ transform: `translateX(${page * -50}%)` }}
        >
          <div className={`${styles.page} ${styles.ringsPage}`}>
            {/* Page 1: BolusRings in cycle-115 rings-only mode
                (`hideContext`) — the "Insulin on board" side column is
                dropped so the ~184px card fits the slim landscape stats
                column with no scaling. The textual insulin/food detail
                lives on page 2. The cycle-43 ActiveBolusesModal tap flow
                is preserved via `onTap`. */}
            <BolusRings
              boluses={boluses}
              now={now}
              basal={basal}
              onDismissDue={onDismissDue}
              onDismissCard={onDismissCard}
              onTap={onRingsTap}
              hideContext
            />
          </div>
          <div className={styles.page}>
            <div className={styles.detailCard} aria-label="Insulin and food">
              {isEmpty ? (
                <div className={styles.detailEmpty}>
                  No recent insulin or food
                </div>
              ) : (
                <>
                  {insulinRows.length > 0 ? (
                    <div className={styles.detailSection}>
                      <div className={styles.detailHeading}>Insulin</div>
                      {insulinRows.map((r) => (
                        <div className={styles.detailRow} key={`i-${r.takenAt}`}>
                          <span
                            className={styles.rowDot}
                            style={{
                              // Cycle 118: point the page-2 tag dots at the
                              // shared --iob-ring-* token pair (via the iob.ts
                              // constants) so the new IOB-ring-color setting
                              // recolors them too. Was --zone-warn / --accent
                              // (the pre-104d colors documented in cycle 114).
                              background: r.isCorrection
                                ? CORRECTION_BOLUS_COLOR
                                : MEAL_BOLUS_COLOR,
                            }}
                          />
                          <span className={styles.rowPrimary}>
                            {r.units.toFixed(1)} u
                          </span>
                          <span className={styles.rowTag}>
                            {r.isCorrection ? "Correction" : "Meal"}
                          </span>
                          <span className={styles.rowMeta}>
                            {fmtTime(r.takenAt, { h24 })} · {fmtRemaining(r.remainingMs)} left
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {visibleFood.length > 0 ? (
                    <div className={styles.detailSection}>
                      <div className={styles.detailHeading}>Food</div>
                      {visibleFood.map((r) => (
                        <div className={styles.detailRow} key={`f-${r.takenAt}`}>
                          <span className={styles.rowPrimary}>
                            {Math.round(r.carbsG)} g
                          </span>
                          <span className={styles.rowMeta}>
                            {fmtTime(r.takenAt, { h24 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {moreCount > 0 ? (
                    <div className={styles.detailMore}>+{moreCount} more</div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.dots}>
        {[0, 1].map((i) => (
          <button
            key={i}
            type="button"
            className={`${styles.dot} ${page === i ? styles.dotActive : ""}`}
            aria-label={i === 0 ? "Insulin rings" : "Insulin & food detail"}
            aria-current={page === i}
            onClick={() => goToPage(i as 0 | 1)}
          >
            <span className={styles.dotMark} />
          </button>
        ))}
      </div>
    </div>
  );
}
